// Draft → Reserved. A confirm dialog: dates, items, total, deposit due, and
// exactly what gets sent (from next_action.effects — the server mirrors the
// parallel-run decision, so a suppressed send says so here).
import React, { useEffect, useState } from 'react';
import { UI, fmtMoney, useIsNarrow } from './pipeline';
import { fmtDate } from '../../lib/fmtDate';

// Draft → Reserved. Optional counter moments live here too: collect the
// booking deposit inline and capture the signature now. Neither gates the
// Reserve button — a phone reservation confirms with both untouched, and the
// Check Out step catches whatever is still open later.
export default function ReserveConfirm({ order, onClose, transition, onDone, onRefresh, openSign, openCollect }) {
  const narrow = useIsNarrow();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [waitingSign, setWaitingSign] = useState(false);
  const signed = !!order.signed_at;
  const paid = Number(order.amount_paid || 0);
  const total = Number(order.total || 0);
  const balance = Math.max(0, Math.round((total - paid) * 100) / 100);
  // While the signature window is open, poll (the sign page's postMessage also refreshes).
  useEffect(() => {
    if (!waitingSign || signed) return undefined;
    const t = setInterval(() => onRefresh && onRefresh(), 3000);
    return () => clearInterval(t);
  }, [waitingSign, signed, onRefresh]);
  const na = order.next_action || {};
  const d = na.dialog || {};
  const items = order.rental_order_items || [];

  async function go() {
    setBusy(true); setErr(null);
    const r = await transition(order, { action: 'reserve' });
    setBusy(false);
    if (r.ok) onDone(r.order); else setErr(r.reason);
  }

  return (
    <div style={UI.overlay(narrow)} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={UI.box(narrow, 480)} data-testid="reserve-dialog" role="dialog">
        <div style={UI.head}>
          <div><div style={UI.title}>Reserve {order.order_number || '#' + order.id}</div><div style={UI.sub}>{order.client_name}</div></div>
          <button style={UI.close} onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div style={UI.body}>
          <div style={UI.row}><span>Dates</span><span style={{ color: '#f1f5f9', fontWeight: 600 }}>{fmtDate(d.from_date || order.from_date)} → {fmtDate(d.to_date || order.to_date)}</span></div>
          {items.map((li, i) => (
            <div key={li.id || i} style={UI.row}><span>{li.item_name} × {li.quantity || 1}</span><span>{fmtMoney(li.total_price)}</span></div>
          ))}
          <div style={{ ...UI.row, fontSize: 14, fontWeight: 800, color: '#f1f5f9' }}><span>Total</span><span style={{ color: '#f97316' }}>{fmtMoney(d.total ?? order.total)}</span></div>
          {/* Booking deposit — from the ledger; optional here */}
          <label style={UI.lbl}>Booking deposit <span style={{ color: '#94a3b8', fontWeight: 400, textTransform: 'none' }}>(optional now — Check Out asks again)</span></label>
          {balance <= 0
            ? <div data-testid="reserve-paid-in-full" style={{ ...UI.row, color: '#4ade80', fontWeight: 700 }}><span>✓ Paid in full</span><span>{fmtMoney(paid)} of {fmtMoney(total)}</span></div>
            : d.deposit_due != null && paid + 0.005 >= Number(d.deposit_due)
              ? <div data-testid="reserve-deposit-paid" style={{ ...UI.row, color: '#4ade80', fontWeight: 700 }}><span>✓ Booking deposit paid</span><span>{fmtMoney(paid)} paid · {fmtMoney(balance)} balance</span></div>
              : <>
                  <div data-testid="reserve-deposit-due" style={UI.row}><span>Booking deposit due</span><span>{d.deposit_due != null ? fmtMoney(d.deposit_due) : '—'}{paid > 0 ? ` · paid ${fmtMoney(paid)}` : ''}</span></div>
                  {openCollect && d.deposit_due != null && <button type="button" data-testid="reserve-collect" onClick={() => openCollect(order, { defaultAmount: Math.min(Number(d.deposit_due), balance) })} style={{ ...UI.cancelBtn, width: '100%', marginTop: 6, color: '#4ade80' }}>💰 Collect deposit {fmtMoney(Math.min(Number(d.deposit_due), balance))}</button>}
                </>}
          {/* Signature — optional here */}
          <label style={UI.lbl}>Contract <span style={{ color: '#94a3b8', fontWeight: 400, textTransform: 'none' }}>(optional now)</span></label>
          {signed
            ? <div data-testid="reserve-signed" style={{ fontSize: 12, color: '#4ade80' }}>✓ Signed {new Date(order.signed_at).toLocaleString()}{order.signer_name ? ` by ${order.signer_name}` : ''}</div>
            : openSign && <>
                <button type="button" data-testid="reserve-sign" onClick={() => { setWaitingSign(true); openSign(order); }} style={{ ...UI.cancelBtn, width: '100%', color: '#fb923c' }}>✍️ Get signature now</button>
                {waitingSign && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>Waiting for the signature… this dialog updates by itself.</div>}
              </>}
          <label style={UI.lbl}>What happens</label>
          {(na.effects || []).map((line, i) => <div key={i} style={UI.effect}>• {line}</div>)}
          {!na.enabled && <div style={{ marginTop: 10, color: '#f87171', fontSize: 12 }}>{na.reason}</div>}
          {err && <div style={{ marginTop: 10, color: '#f87171', fontSize: 12 }} data-testid="dialog-error">{err}</div>}
        </div>
        <div style={UI.foot}>
          <button style={UI.cancelBtn} onClick={onClose}>Back</button>
          <button data-testid="reserve-confirm" disabled={busy || !na.enabled} onClick={go} style={UI.goBtn(!busy && na.enabled, 'linear-gradient(135deg,#f97316,#ea580c)', '#fff')}>
            {busy ? 'Reserving…' : na.label || 'Reserve & send confirmation'}
          </button>
        </div>
      </div>
    </div>
  );
}
